import { Link } from "@tanstack/react-router";
import {
  ArrowRightIcon,
  CheckCircle2Icon,
  CircleDashedIcon,
  PauseCircleIcon,
  PlugIcon,
} from "lucide-react";

import { trpc } from "@blis/trpc/react";

import { LoadingPage } from "@/components/LoadingPage";
import { Badge } from "@/components/ui/badge";
import { SubHeading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import { Section } from "../components/Section";

const KIND_TO_ROUTE: Record<string, string> = {
  ITRANSPLANT: "/admin/integrations/itransplant",
};

export function IntegrationsPage() {
  const { data, isLoading } = trpc.integrations.list.useQuery();

  if (isLoading || !data) return <LoadingPage />;

  return (
    <>
      <Section.Title>Integrations</Section.Title>
      <SubHeading className="mb-6 max-w-2xl">
        External systems bLIS can send data to. Enable the integration globally,
        then configure and enable each organization that should send
        (credentials are per-org).
      </SubHeading>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.map((integration) => {
          const href = KIND_TO_ROUTE[integration.kind];
          return (
            <IntegrationCard
              key={integration.kind}
              label={integration.label}
              description={integration.description}
              configured={integration.configured}
              enabled={integration.enabled}
              enabledOrgCount={integration.enabledOrgCount}
              href={href}
            />
          );
        })}
      </div>
    </>
  );
}

function IntegrationCard({
  label,
  description,
  configured,
  enabled,
  enabledOrgCount,
  href,
}: {
  label: string;
  description: string;
  configured: boolean;
  enabled: boolean;
  enabledOrgCount: number;
  href?: string;
}) {
  const status: "active" | "disabled" | "unconfigured" = !configured
    ? "unconfigured"
    : enabled
      ? "active"
      : "disabled";

  const subtitle =
    status === "active"
      ? `Globally enabled · ${enabledOrgCount} org${enabledOrgCount === 1 ? "" : "s"}`
      : status === "disabled"
        ? "Orgs configured · globally disabled"
        : "Awaiting organization setup";

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-md border bg-muted/40 text-muted-foreground">
            <PlugIcon className="size-4" />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold tracking-tight truncate">
              {label}
            </div>
            <Text size="1" muted className="truncate">
              {subtitle}
            </Text>
          </div>
        </div>
        {status === "active" ? (
          <Badge color="green" className="gap-1 shrink-0">
            <CheckCircle2Icon className="size-3" />
            Enabled
          </Badge>
        ) : status === "disabled" ? (
          <Badge color="gray" className="gap-1 shrink-0">
            <PauseCircleIcon className="size-3" />
            Disabled
          </Badge>
        ) : (
          <Badge color="amber" className="gap-1 shrink-0">
            <CircleDashedIcon className="size-3" />
            Not configured
          </Badge>
        )}
      </div>

      <Text muted className="mt-3 line-clamp-2 min-h-[2.5em]">
        {description}
      </Text>

      <div className="mt-4 flex items-center justify-between gap-2 border-t pt-3">
        <Text size="2" muted className="tabular-nums">
          Enabled for{" "}
          <span className="font-medium text-foreground">{enabledOrgCount}</span>{" "}
          {enabledOrgCount === 1 ? "organization" : "organizations"}
        </Text>
        {href && (
          <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground/80 group-hover:text-foreground">
            Manage
            <ArrowRightIcon className="size-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        )}
      </div>
    </>
  );

  const baseClass =
    "rounded-lg border bg-background p-5 shadow-xs transition-colors";

  if (!href) {
    return <div className={baseClass}>{body}</div>;
  }

  return (
    <Link
      to={href}
      className={`group ${baseClass} hover:border-foreground/20 hover:bg-accent/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring`}
    >
      {body}
    </Link>
  );
}
